import { useEffect, useMemo, useState } from 'react';

import type { ShoppingList } from './itemsApi.ts';

/**
 * Which of the group's lists is showing.
 *
 * Remembered per group, because "the DIY list" is a different preference from "the
 * weekly shop", and someone who was last looking at one should find it again rather
 * than being dropped back on the default every time.
 *
 * Derived rather than synchronised: the stored id is a *preference*, and what actually
 * shows is whatever it resolves to against the lists we have. A stored id for a list
 * that has since been deleted simply falls back to the default and self-heals.
 *
 * The caller is expected to remount this per group (see the `key` on ListView), so
 * there is no cross-group leakage to reset.
 */
export function useSelectedList(groupId: string, lists: ShoppingList[] | undefined) {
  const [preferredId, setPreferredId] = useState<string | null>(() => readSelectedList(groupId));

  const selected = useMemo(() => {
    if (!lists || lists.length === 0) {
      return null;
    }

    return (
      lists.find((list) => list.id === preferredId) ??
      lists.find((list) => list.isDefault) ??
      lists[0] ??
      null
    );
  }, [lists, preferredId]);

  // localStorage is an external system, so keeping it in step is what an effect is for.
  // Only the resolved value is persisted, so a stale id cannot linger.
  useEffect(() => {
    if (selected) {
      persistSelectedList(groupId, selected.id);
    }
  }, [groupId, selected]);

  return { selected, select: setPreferredId };
}

function storageKey(groupId: string): string {
  return `bnb.selectedListId:${groupId}`;
}

function readSelectedList(groupId: string): string | null {
  try {
    return localStorage.getItem(storageKey(groupId));
  } catch {
    return null;
  }
}

function persistSelectedList(groupId: string, listId: string): void {
  try {
    localStorage.setItem(storageKey(groupId), listId);
  } catch {
    // Non-fatal: the choice just will not survive a reload.
  }
}
